// Jobs at shops that aren't phone shops: a mechanic's work order for a vehicle, and repair
// tickets for vacuums and computers. They're the same records as phone repairs (a ticket, a
// customer, lines, a status, a payment), with what each kind of shop writes down at drop-off.
// Everything here is typed in by the counter: there is no VIN decoding, vehicle history,
// diagnostics or parts catalog behind it.

export type JobKind = 'vehicle' | 'vacuum' | 'computer';
export const JOB_KINDS: JobKind[] = ['vehicle', 'vacuum', 'computer'];

export function isJobKind(v: unknown): v is JobKind {
  return typeof v === 'string' && (JOB_KINDS as string[]).includes(v);
}

/** The kind of job a shop writes up (null: a phone shop, with the phone check-in). */
export function jobKindForIndustry(industry: string | null | undefined): JobKind | null {
  return industry === 'auto' ? 'vehicle' : industry === 'vacuum' ? 'vacuum' : industry === 'computer' ? 'computer' : null;
}

export interface IntakeItem { key: string; label: string }

/** The drop-off check for each kind (OK, problem, or not checked), like the phone check-in's. */
export const INTAKE_ITEMS: Record<JobKind, IntakeItem[]> = {
  vehicle: [
    { key: 'starts', label: 'Starts and runs' },
    { key: 'warning_lights', label: 'No warning lights on the dash' },
    { key: 'brakes', label: 'Brakes' },
    { key: 'tires', label: 'Tires' },
    { key: 'lights', label: 'Lights and signals' },
    { key: 'body', label: 'Body: no damage' },
    { key: 'glass', label: 'Windshield and glass' },
    { key: 'interior', label: 'Interior' },
  ],
  vacuum: [
    { key: 'powers_on', label: 'Turns on' },
    { key: 'suction', label: 'Suction' },
    { key: 'brush_roll', label: 'Brush roll spins' },
    { key: 'cord_battery', label: 'Cord or battery' },
    { key: 'hose', label: 'Hose and wand clear' },
    { key: 'filters', label: 'Filters' },
    { key: 'belt', label: 'Belt' },
    { key: 'housing', label: 'Housing: no cracks' },
  ],
  computer: [
    { key: 'powers_on', label: 'Turns on' },
    { key: 'screen', label: 'Screen' },
    { key: 'keyboard', label: 'Keyboard and trackpad' },
    { key: 'charging', label: 'Charges' },
    { key: 'ports', label: 'Ports' },
    { key: 'wifi', label: 'Wi-Fi' },
    { key: 'housing', label: 'Case: no cracks' },
  ],
};

/** The answers on these checks: OK, a problem, or not checked. */
export const INTAKE_VALUE_LABELS: Record<'ok' | 'bad' | 'na', string> = { ok: 'OK', bad: 'Problem', na: 'Not checked' };

/** What's usually left with it at drop-off. */
export const LEFT_WITH_BY_KIND: Record<JobKind, string[]> = {
  vehicle: ['Keys', 'Key fob', 'Wheel lock key', 'Spare tire', 'Personal items in the vehicle'],
  vacuum: ['Attachments', 'Hose', 'Wand', 'Battery', 'Charger', 'Bags', 'Filters', 'Box'],
  computer: ['Charger', 'Bag', 'Mouse', 'Case', 'External drive'],
};

export const VACUUM_TYPES = ['Upright', 'Canister', 'Stick', 'Handheld', 'Robot', 'Backpack', 'Central vac', 'Wet/dry'];
export const COMPUTER_TYPES = ['Laptop', 'Desktop', 'All-in-one', 'Tablet', 'Other'];

/** The words on the screens for each kind. */
export const JOB_WORDS: Record<JobKind, { job: string; Job: string; item: string; Item: string; checkIn: string }> = {
  vehicle: { job: 'work order', Job: 'Work order', item: 'vehicle', Item: 'Vehicle', checkIn: 'New work order' },
  vacuum: { job: 'repair', Job: 'Repair', item: 'vacuum', Item: 'Vacuum', checkIn: 'Check in a vacuum' },
  computer: { job: 'repair', Job: 'Repair', item: 'computer', Item: 'Computer', checkIn: 'Check in a computer' },
};

/** "2018 Toyota Camry", "Dyson V11", "Dell XPS 13". */
export function jobItemName(j: { brand?: string | null; model?: string | null; vehicle_year?: number | null }): string {
  return [j.vehicle_year ? String(j.vehicle_year) : '', (j.brand ?? '').trim(), (j.model ?? '').trim()].filter(Boolean).join(' ');
}

// ---------------------------------------------------------------- VIN (typed in; never decoded)

/** Upper case, no spaces or dashes. */
export function cleanVin(v: string | null | undefined): string {
  return (v ?? '').toUpperCase().replace(/[\s-]/g, '');
}

/** A VIN as typed: letters and numbers without I, O or Q; 17 since 1981 (older ones are 11 to 17). */
export function vinLooksRight(vin: string): boolean {
  return /^[A-HJ-NPR-Z0-9]{11,17}$/.test(vin);
}

const VIN_VALUES: Record<string, number> = {
  A: 1, B: 2, C: 3, D: 4, E: 5, F: 6, G: 7, H: 8, J: 1, K: 2, L: 3, M: 4, N: 5, P: 7, R: 9, S: 2, T: 3, U: 4, V: 5, W: 6, X: 7, Y: 8, Z: 9,
};
const VIN_WEIGHTS = [8, 7, 6, 5, 4, 3, 2, 10, 0, 9, 8, 7, 6, 5, 4, 3, 2];

/**
 * The check digit (the 9th character) of a 17-character North American VIN. A mismatch usually
 * means a typo; vehicles made for other markets may not use it, so it's a warning, never a block.
 */
export function vinCheckDigitOk(vin: string): boolean | null {
  if (vin.length !== 17 || !vinLooksRight(vin)) return null;
  let sum = 0;
  for (let i = 0; i < 17; i++) {
    const ch = vin[i];
    const v = /\d/.test(ch) ? Number(ch) : VIN_VALUES[ch];
    if (v === undefined) return null;
    sum += v * VIN_WEIGHTS[i];
  }
  const r = sum % 11;
  return vin[8] === (r === 10 ? 'X' : String(r));
}

// ---------------------------------------------------------------- lines: parts and labor

export type LineKind = 'part' | 'labor';

/** Labor: hours × rate, to the cent. */
export function laborPrice(hours: number, rateCents: number): number {
  return Math.round(hours * rateCents);
}

/** "1.5 h", "0.25 h". */
export function hoursText(hoursX100: number | null | undefined): string {
  if (!hoursX100) return '';
  return `${Number((hoursX100 / 100).toFixed(2))} h`;
}

/** How a customer approved (or declined) an estimate. */
export const APPROVAL_METHODS = ['in_person', 'phone', 'text', 'email'] as const;
export type ApprovalMethod = (typeof APPROVAL_METHODS)[number];
export const APPROVAL_METHOD_LABELS: Record<ApprovalMethod, string> = {
  in_person: 'In person (signed)', phone: 'By phone', text: 'By text', email: 'By email',
};
